import { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { VoiceNoteButton } from '@/components/capture/VoiceNoteButton';
import { cn } from '@/lib/utils';
import type { RerunIteration, RerunRequest } from '@/lib/api/types';
import { TRIGGER_LABEL, humanise, type AttributeView } from './review-model';

interface RefinePanelProps {
  captureId: string;
  attributes: AttributeView[];
  iterations: RerunIteration[];
  /** False outside SCORED, IN_REVIEW and RECAPTURE_REQUESTED. */
  canRerun: boolean;
  busy: boolean;
  onRerun: (request: RerunRequest) => Promise<boolean>;
  /** Read the record again once a CONTEXT note is registered. */
  onVoiceRecorded: () => void;
}

function timeOf(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' });
}

/**
 * Refine with the AI, the third block of the Base44 review: pick the fields
 * to re-analyse (none picked means every attribute), add a typed or spoken
 * hint, and Rerun AI. The history below shows each earlier pass with the
 * values it replaced, so a rerun that made things worse is visible.
 */
export function RefinePanel({ captureId, attributes, iterations, canRerun, busy, onRerun, onVoiceRecorded }: RefinePanelProps) {
  const [fields, setFields] = useState<string[]>([]);
  const [context, setContext] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  function toggle(name: string) {
    setFields((current) => (current.includes(name) ? current.filter((item) => item !== name) : [...current, name]));
  }

  async function rerun() {
    const accepted = await onRerun({
      fields,
      ...(context.trim() ? { context: context.trim() } : {}),
      includeVoiceContext: true,
    });
    if (accepted) {
      setFields([]);
      setContext('');
    }
  }

  const history = [...iterations].sort((a, b) => b.iteration - a.iteration);

  return (
    <section aria-label="Refine with AI" className="flex flex-col gap-3 rounded-card border border-border p-3 sm:p-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-sm font-semibold">Refine with AI</h2>
        <p className="text-xs text-muted">
          Pick the fields to read again, or none to read every field. A hint tells the AI what to look for.
        </p>
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="mb-2 text-xs text-muted">Fields to re-analyse</legend>
        <div className="grid gap-1.5 sm:grid-cols-2">
          {attributes.map((attribute) => {
            const checked = fields.includes(attribute.name);
            return (
              <label
                key={attribute.name}
                className={cn(
                  'flex min-h-11 cursor-pointer items-center gap-3 rounded-control border px-3 text-sm',
                  checked ? 'border-accent' : 'border-border',
                )}
              >
                <input
                  type="checkbox"
                  className="size-4 accent-[var(--color-accent)]"
                  checked={checked}
                  disabled={busy || !canRerun}
                  onChange={() => toggle(attribute.name)}
                />
                {humanise(attribute.name)}
              </label>
            );
          })}
        </div>
        <p className="text-xs text-muted">
          {fields.length === 0 ? 'Every attribute will be read again.' : `${fields.length} selected.`}
        </p>
      </fieldset>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="refine-context" className="text-xs text-muted">
          Hint for the AI
        </label>
        <textarea
          id="refine-context"
          rows={3}
          value={context}
          disabled={busy || !canRerun}
          onChange={(event) => setContext(event.target.value)}
          placeholder="For example: the model number is on the rating plate, bottom left"
          className="w-full rounded-control border border-border bg-transparent px-3 py-2 text-sm text-surface-foreground placeholder:text-muted focus-visible:border-brand focus-visible:outline-none disabled:opacity-50"
        />
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-xs text-muted">
          <VoiceNoteButton purpose="CONTEXT" captureId={captureId} onRecorded={onVoiceRecorded} />
          <span>Speak a hint instead</span>
        </div>
        <Button type="button" disabled={busy || !canRerun} onClick={() => void rerun()}>
          <RefreshCw aria-hidden="true" />
          Rerun AI
        </Button>
      </div>
      {!canRerun && (
        <p className="text-xs text-muted">Rerun AI is available while the capture is waiting for review.</p>
      )}

      {history.length > 0 && (
        <div className="flex flex-col gap-2 border-t border-border pt-3">
          <button
            type="button"
            aria-expanded={showHistory}
            onClick={() => setShowHistory((value) => !value)}
            className="flex min-h-11 w-fit items-center text-sm text-accent underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            {showHistory ? 'Hide' : 'Show'} iteration history ({history.length})
          </button>
          {showHistory && (
            <ol className="flex flex-col gap-2">
              {history.map((iteration) => (
                <li key={iteration.iteration} className="flex flex-col gap-1 rounded-card border border-border p-3 text-xs">
                  <span className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-sm text-surface-foreground">
                      Pass {iteration.iteration}: {TRIGGER_LABEL[iteration.trigger]}
                    </span>
                    <span className="text-muted">
                      {timeOf(iteration.requestedAt)}
                      {!iteration.completedAt && ', still reading'}
                    </span>
                  </span>
                  <span className="text-muted">
                    {iteration.fields.length === 0
                      ? 'Every attribute'
                      : iteration.fields.map((name) => humanise(name)).join(', ')}
                  </span>
                  {iteration.context && <span>Hint: &ldquo;{iteration.context}&rdquo;</span>}
                  {Object.keys(iteration.before).length > 0 && (
                    <span className="text-muted">
                      Before:{' '}
                      {Object.entries(iteration.before)
                        .map(([name, value]) => `${humanise(name)} ${value === null ? 'empty' : String(value)}`)
                        .join(', ')}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}
